"use client"

import React, { useState } from "react"
import { X, Clock, TrendingUp, Tag, Trash2, Heart } from "@/lib/icons"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useSavedTagsStore } from "@/lib/saved-tags-store"
import { useTagFiltersStore } from "@/lib/tag-filters-store"
import { tagStartsWith } from "@/lib/tag-suggestions"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface SearchConsoleModalProps {
  isOpen: boolean
  onClose: () => void
  onTagSelect: (tag: string) => void
  onMultiTagSelect: (tags: string[]) => void
}

export function SearchConsoleModal({ isOpen, onClose, onTagSelect, onMultiTagSelect }: SearchConsoleModalProps) {
  const tt = useTranslations("searchConsoleModal")
  const { savedTags, removeTag, getRecentTags, getFrequentTags, getAllTags } = useSavedTagsStore()
  const { tagFilters } = useTagFiltersStore()
  const favoriteTags = tagFilters.filter(f => f.type === "love")
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [searchFilter, setSearchFilter] = useState("")

  const recentTags = getRecentTags(20)
  const frequentTags = getFrequentTags(20)
  const allTags = getAllTags()

  const filteredTags = (tags: typeof savedTags) => {
    if (!searchFilter) return tags
    // Tags starting with what was typed — same rule as the search bars ("mi" no longer
    // matches the middle of "admiral"), keeping each tab's own order.
    return tags.filter((tag) => tagStartsWith(searchFilter, tag.tag))
  }

  const handleTagClick = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter((t) => t !== tag))
    } else {
      setSelectedTags([...selectedTags, tag])
    }
  }

  const handleSingleTagSelect = (tag: string) => {
    onTagSelect(tag)
    onClose()
  }

  const handleMultiSearch = () => {
    if (selectedTags.length > 0) {
      onMultiTagSelect(selectedTags)
      setSelectedTags([])
      onClose()
    }
  }

  const handleClearSelection = () => {
    setSelectedTags([])
  }

  const TagItem = ({ tag, showFrequency = false }: { tag: typeof savedTags[0]; showFrequency?: boolean }) => (
    <div className="flex items-center gap-2 group">
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          "flex-1 justify-start h-8 px-2 text-xs",
          selectedTags.includes(tag.tag) && "bg-primary text-primary-foreground"
        )}
        onClick={() => handleTagClick(tag.tag)}
        onDoubleClick={() => handleSingleTagSelect(tag.tag)}
      >
        <span className="truncate">{tag.tag.replace(/_/g, " ")}</span>
        {showFrequency && (
          <Badge variant="secondary" className="ml-auto text-[10px] h-4 px-1">
            {tag.frequency}
          </Badge>
        )}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 hover:bg-destructive hover:text-destructive-foreground"
        onClick={() => removeTag(tag.id)}
      >
        <Trash2 className="h-3 w-3" />
      </Button>
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            {tt("searchConsole")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-3 min-h-0 overflow-hidden">
          <Input
            placeholder={tt("filterTags")}
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="h-8"
          />

          {selectedTags.length > 0 && (
            <div className="flex items-center gap-2 p-2 bg-secondary/50 rounded-md">
              <span className="text-xs text-muted-foreground">{tt("selected")}</span>
              <div className="flex flex-wrap gap-1 flex-1">
                {selectedTags.map((tag) => (
                  <Badge key={tag} variant="default" className="text-xs">
                    {tag.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
              <Button size="sm" variant="ghost" onClick={handleClearSelection} className="h-6 px-2">
                {tt("clear")}
              </Button>
            </div>
          )}

          <Tabs defaultValue="recent" className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="recent" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {tt("recent")}
              </TabsTrigger>
              <TabsTrigger value="frequent" className="text-xs">
                <TrendingUp className="h-3 w-3 mr-1" />
                {tt("popular")}
              </TabsTrigger>
              <TabsTrigger value="favorites" className="text-xs">
                <Heart className="h-3 w-3 mr-1" />
                {tt("loved")}
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs">
                {tt("allLength", { length: allTags.length })}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="recent" className="flex-1 min-h-0 mt-2">
              <div className="h-full overflow-y-auto space-y-1 pr-1">
                {filteredTags(recentTags).map((tag) => (
                  <TagItem key={tag.id} tag={tag} />
                ))}
                {filteredTags(recentTags).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    {searchFilter ? tt("noMatchingRecentTags") : tt("noRecentTags")}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="frequent" className="flex-1 min-h-0 mt-2">
              <div className="h-full overflow-y-auto space-y-1 pr-1">
                {filteredTags(frequentTags).map((tag) => (
                  <TagItem key={tag.id} tag={tag} showFrequency />
                ))}
                {filteredTags(frequentTags).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    {searchFilter ? tt("noMatchingPopularTags") : tt("noPopularTags")}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="favorites" className="flex-1 min-h-0 mt-2">
              <div className="h-full overflow-y-auto space-y-1 pr-1">
                {favoriteTags
                  .filter(f => !searchFilter || tagStartsWith(searchFilter, f.tag))
                  .map((f) => (
                    <div key={f.tag} className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "flex-1 justify-start h-8 px-2 text-xs",
                          selectedTags.includes(f.tag) && "bg-primary text-primary-foreground"
                        )}
                        onClick={() => handleTagClick(f.tag)}
                        onDoubleClick={() => handleSingleTagSelect(f.tag)}
                      >
                        <Heart className="h-3 w-3 mr-1 text-rose-400 flex-shrink-0" />
                        <span className="truncate">{f.tag.replace(/_/g, " ")}</span>
                      </Button>
                    </div>
                  ))}
                {favoriteTags.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    {tt("noLovedTagsYetHeartTagsInTheTagFilter")}
                  </p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="all" className="flex-1 min-h-0 mt-2">
              <div className="h-full overflow-y-auto space-y-1 pr-1">
                {filteredTags(allTags).map((tag) => (
                  <TagItem key={tag.id} tag={tag} showFrequency />
                ))}
                {filteredTags(allTags).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    {searchFilter ? tt("noMatchingTags") : tt("noSavedTags")}
                  </p>
                )}
              </div>
            </TabsContent>
          </Tabs>

          {selectedTags.length > 0 && (
            <div className="flex gap-2">
              <Button onClick={handleMultiSearch} className="flex-1" size="sm">
                {tt("searchSelectedLength", { length: selectedTags.length })}
              </Button>
            </div>
          )}

          <div className="text-xs text-muted-foreground text-center">
            {tt("singleClickToSelectDoubleClickToSearch")}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}